import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Package, Plus, Pencil, Trash2, X, Save, Tag } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/api'

const AdminDashboard = () => {
  const { user, isAdmin, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingProduct, setEditingProduct] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [activeTab, setActiveTab] = useState('products')
  const [showCategoryForm, setShowCategoryForm] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [categoryFormData, setCategoryFormData] = useState({ name: '', display_order: 0 })
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    price: '',
    original_price: '',
    image: '',
    description: '',
    product_details: '',
    material: '',
    badge: '',
    special: false,
    in_stock: true,
    available_slots: '',
    total_slots: ''
  })

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      navigate('/login')
      return
    }
    if (user && isAdmin) {
      fetchProducts()
      fetchCategories()
    }
  }, [user, isAdmin, authLoading])

  const fetchProducts = async () => {
    try {
      const res = await api.get('/admin/products')
      setProducts(res.data)
    } catch (err) {
      toast.error('Failed to load products')
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const res = await api.get('/admin/categories')
      setCategories(res.data)
    } catch (err) {
      // Categories may not exist yet
    }
  }

  // Category handlers
  const handleAddCategory = () => {
    setShowCategoryForm(true)
    setEditingCategory(null)
    setCategoryFormData({ name: '', display_order: categories.length })
  }

  const handleEditCategory = (category) => {
    setEditingCategory(category.id)
    setShowCategoryForm(true)
    setCategoryFormData({ name: category.name, display_order: category.display_order })
  }

  const handleCancelCategory = () => {
    setShowCategoryForm(false)
    setEditingCategory(null)
    setCategoryFormData({ name: '', display_order: 0 })
  }

  const handleSaveCategory = async () => {
    if (!categoryFormData.name.trim()) {
      toast.error('Please enter a category name')
      return
    }
    try {
      if (editingCategory) {
        await api.put(`/admin/categories/${editingCategory}`, categoryFormData)
        toast.success('Category updated')
      } else {
        await api.post('/admin/categories', categoryFormData)
        toast.success('Category created')
      }
      handleCancelCategory()
      fetchCategories()
    } catch (err) {
      // error handled by interceptor
    }
  }

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return
    try {
      await api.delete(`/admin/categories/${id}`)
      toast.success('Category deleted')
      fetchCategories()
    } catch (err) {
      // error handled by interceptor
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      price: '',
      original_price: '',
      image: '',
      description: '',
      product_details: '',
      material: '',
      badge: '',
      special: false,
      in_stock: true,
      available_slots: '',
      total_slots: ''
    })
  }

  const handleEdit = (product) => {
    setEditingProduct(product.id)
    setShowAddForm(false)
    setFormData({
      name: product.name,
      category: product.category,
      price: product.price.toString(),
      original_price: product.original_price?.toString() || '',
      image: product.image,
      description: product.description,
      product_details: product.product_details || '',
      material: product.material || '',
      badge: product.badge || '',
      special: product.special,
      in_stock: product.in_stock,
      available_slots: product.available_slots.toString(),
      total_slots: product.total_slots.toString()
    })
  }

  const handleAdd = () => {
    setShowAddForm(true)
    setEditingProduct(null)
    resetForm()
  }

  const handleCancel = () => {
    setEditingProduct(null)
    setShowAddForm(false)
    resetForm()
  }

  const buildPayload = () => {
    const payload = {
      name: formData.name,
      category: formData.category,
      price: parseInt(formData.price),
      image: formData.image,
      description: formData.description,
      special: formData.special,
      in_stock: formData.in_stock,
      available_slots: parseInt(formData.available_slots) || 0,
      total_slots: parseInt(formData.total_slots) || 0
    }
    if (formData.original_price) payload.original_price = parseInt(formData.original_price)
    if (formData.product_details) payload.product_details = formData.product_details
    if (formData.material) payload.material = formData.material
    if (formData.badge) payload.badge = formData.badge
    return payload
  }

  const handleSave = async () => {
    if (!formData.name || !formData.category || !formData.price || !formData.image || !formData.description) {
      toast.error('Please fill required fields (name, category, price, image, description)')
      return
    }
    try {
      if (editingProduct) {
        await api.put(`/admin/products/${editingProduct}`, buildPayload())
        toast.success('Product updated')
      } else {
        await api.post('/admin/products', buildPayload())
        toast.success('Product created')
      }
      handleCancel()
      fetchProducts()
    } catch (err) {
      // error handled by interceptor
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return
    try {
      await api.delete(`/admin/products/${id}`)
      toast.success('Product deleted')
      fetchProducts()
    } catch (err) {
      // error handled by interceptor
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600 text-lg">Loading...</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-champagne-600" />
              <div>
                <h1 className="font-serif text-3xl font-bold text-gray-800">Admin Dashboard</h1>
                <p className="text-gray-600">Manage your products and categories</p>
              </div>
            </div>
            {activeTab === 'products' ? (
              <button
                onClick={handleAdd}
                className="flex items-center gap-2 bg-champagne-600 text-white px-5 py-2.5 rounded-lg hover:bg-champagne-700 transition-colors font-medium"
              >
                <Plus className="w-5 h-5" />
                Add Product
              </button>
            ) : (
              <button
                onClick={handleAddCategory}
                className="flex items-center gap-2 bg-champagne-600 text-white px-5 py-2.5 rounded-lg hover:bg-champagne-700 transition-colors font-medium"
              >
                <Plus className="w-5 h-5" />
                Add Category
              </button>
            )}
          </div>
          {/* Tabs */}
          <div className="flex gap-4 mt-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('products')}
              className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'products'
                  ? 'border-champagne-600 text-champagne-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Products ({products.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`pb-3 px-1 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'categories'
                  ? 'border-champagne-600 text-champagne-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Categories ({categories.length})
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <>
            {/* Category Form */}
            {showCategoryForm && (
              <div className="bg-white rounded-xl shadow-sm p-6 mb-8 border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-serif text-xl font-semibold text-gray-800">
                    {editingCategory ? 'Edit Category' : 'Add New Category'}
                  </h2>
                  <button onClick={handleCancelCategory} className="text-gray-500 hover:text-gray-700">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category Name *</label>
                    <input
                      type="text"
                      value={categoryFormData.name}
                      onChange={(e) => setCategoryFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-champagne-500 focus:border-transparent outline-none"
                      placeholder="e.g. Traditional, Bridal, Casual"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                    <input
                      type="number"
                      value={categoryFormData.display_order}
                      onChange={(e) => setCategoryFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-champagne-500 focus:border-transparent outline-none"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={handleCancelCategory}
                    className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveCategory}
                    className="flex items-center gap-2 bg-champagne-600 text-white px-5 py-2 rounded-lg hover:bg-champagne-700 transition-colors font-medium"
                  >
                    <Save className="w-4 h-4" />
                    {editingCategory ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            )}

            {/* Categories List */}
            {categories.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 text-center py-12 text-gray-500">
                No categories found. Click "Add Category" to create one.
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-600">Name</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-600">Display Order</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {categories.map((category) => (
                      <tr key={category.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Tag className="w-4 h-4 text-champagne-600" />
                            <span className="font-medium text-gray-800">{category.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-600">{category.display_order}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditCategory(category)}
                              className="p-2 text-gray-600 hover:text-champagne-600 hover:bg-champagne-50 rounded-lg transition-colors"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(category.id)}
                              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <>
        {/* Add/Edit Form */}
        {(showAddForm || editingProduct) && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8 border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-xl font-semibold text-gray-800">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-champagne-500 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-champagne-500 focus:border-transparent outline-none"
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-champagne-500 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Original Price</label>
                <input
                  type="number"
                  value={formData.original_price}
                  onChange={(e) => handleChange('original_price', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-champagne-500 focus:border-transparent outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Image URL *</label>
                <input
                  type="text"
                  value={formData.image}
                  onChange={(e) => handleChange('image', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-champagne-500 focus:border-transparent outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-champagne-500 focus:border-transparent outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Details</label>
                <textarea
                  value={formData.product_details}
                  onChange={(e) => handleChange('product_details', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-champagne-500 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Material</label>
                <input
                  type="text"
                  value={formData.material}
                  onChange={(e) => handleChange('material', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-champagne-500 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Badge</label>
                <input
                  type="text"
                  value={formData.badge}
                  onChange={(e) => handleChange('badge', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-champagne-500 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Available Slots</label>
                <input
                  type="number"
                  value={formData.available_slots}
                  onChange={(e) => handleChange('available_slots', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-champagne-500 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Slots</label>
                <input
                  type="number"
                  value={formData.total_slots}
                  onChange={(e) => handleChange('total_slots', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-champagne-500 focus:border-transparent outline-none"
                />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.special}
                    onChange={(e) => handleChange('special', e.target.checked)}
                    className="w-4 h-4 text-champagne-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Special</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.in_stock}
                    onChange={(e) => handleChange('in_stock', e.target.checked)}
                    className="w-4 h-4 text-champagne-600 rounded"
                  />
                  <span className="text-sm text-gray-700">In Stock</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleCancel}
                className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 bg-champagne-600 text-white px-5 py-2 rounded-lg hover:bg-champagne-700 transition-colors font-medium"
              >
                <Save className="w-4 h-4" />
                {editingProduct ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        )}

        {/* Products - Cards on mobile, Table on desktop */}
        {products.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 text-center py-12 text-gray-500">
            No products found. Click "Add Product" to create one.
          </div>
        ) : (
          <>
            {/* Mobile Cards */}
            <div className="space-y-4 md:hidden">
              {products.map((product) => (
                <div key={product.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                  <div className="flex items-start gap-3">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                      onError={(e) => {
                        e.target.src = 'https://images.pexels.com/photos/1454171/pexels-photo-1454171.jpeg?auto=compress&cs=tinysrgb&w=100'
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate">{product.name}</p>
                      <p className="text-sm text-champagne-600">{product.category}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-semibold text-gray-800">₹{product.price.toLocaleString()}</span>
                        {product.original_price && (
                          <span className="text-xs text-gray-400 line-through">₹{product.original_price.toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500">Slots: {product.available_slots}/{product.total_slots}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${product.in_stock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {product.in_stock ? 'In Stock' : 'Out'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEdit(product)}
                        className="p-2.5 text-champagne-600 bg-champagne-50 rounded-lg active:bg-champagne-100 transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="p-2.5 text-red-600 bg-red-50 rounded-lg active:bg-red-100 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-600">Image</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-600">Name</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-600">Category</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-600">Price</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-600">Slots</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-600">Stock</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {products.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded-lg"
                            onError={(e) => {
                              e.target.src = 'https://images.pexels.com/photos/1454171/pexels-photo-1454171.jpeg?auto=compress&cs=tinysrgb&w=100'
                            }}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-gray-800">{product.name}</p>
                          <p className="text-xs text-gray-500 line-clamp-1">{product.description}</p>
                        </td>
                        <td className="px-6 py-4 text-gray-600">{product.category}</td>
                        <td className="px-6 py-4">
                          <span className="font-medium text-gray-800">₹{product.price.toLocaleString()}</span>
                          {product.original_price && (
                            <span className="text-xs text-gray-400 line-through ml-1">₹{product.original_price.toLocaleString()}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {product.available_slots}/{product.total_slots}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${product.in_stock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {product.in_stock ? 'In Stock' : 'Out of Stock'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(product)}
                              className="p-2 text-gray-600 hover:text-champagne-600 hover:bg-champagne-50 rounded-lg transition-colors"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(product.id)}
                              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
          </>
        )}
      </div>
    </div>
  )
}

export default AdminDashboard
